import StickyButton from '@/components/atom/buttons/StickyButton';
import ClickLogger from '@/components/common/ClickLogger';
import Loader from '@/components/molecules/loader';
import RequestSection from '@/components/templates/diagnosis/treatment-request/components/RequestSection';
import { EWebTreatmentRequestLog, TreatmentRequestLog } from '@/entities/diagnosis/libs';
import { CardCompanyButtonType } from '@/hooks/diagnosis/useTreatmentRequestViewModel';
import { InstallmentSelectItem, TreatmentRequestAgreement, UserPaymentsInfo } from '@/models/common';
import { NonFaceToFaceBookingSchedulesInfo, TreatmentRequest, TreatmentRequestDoctor } from '@/models/diagnosis';
import { User } from '@/types/Auth';
import { Button } from 'doctornow-design-system';
import { RefObject, useMemo } from 'react';
import styled from 'styled-components';
import CardInstallmentBottomSheet from './CardInstallmentBottomSheet';
import CardRegisterBottomSheet from './CardRegisterBottomSheet';

interface Props {
  cardListWrapperRef: RefObject<HTMLDivElement>;
  isUserPaymentsInfoRefetching: boolean;
  isUserPaymentsInfoFetched: boolean;
  point: number;
  cardCompanyButton?: CardCompanyButtonType;
  treatmentRequest: TreatmentRequest;
  userPaymentsInfo: UserPaymentsInfo;
  doctor?: TreatmentRequestDoctor;
  requestAgreementList: TreatmentRequestAgreement[];
  isRequestDiagnosisLoading: boolean;
  bookingSchedulesInfo: NonFaceToFaceBookingSchedulesInfo;
  doctorPublicId: string | null;
  user: User | null | undefined;
  selectCard: (paymentId: string) => void;
  selectCardCompanyButton: (company: CardCompanyButtonType) => void;
  toggleRequestUsePoint: () => void;
  openCardInstallmentListBottomSheet: () => void;
  addCreditCard: () => void;
  addKakaopay: () => void;
  toggleTreatmentRequestAgreement: (agreement: TreatmentRequestAgreement) => void;
  toggleTreatmentRequestAllAgreement: () => void;
  moveToAgreementLink: (url: string) => void;
  onSubmitRequestDiagnosis: () => void;
  scrollToBottomAndCheckAllAgreement: () => void;
  isCardRegisterBottomSheetOpen: boolean;
  onCloseCardRegisterBottomSheet: () => void;
  onSuccessCardRegister: () => void;
  isCardInstallmentBottomSheetOpen: boolean;
  cardInstallments: InstallmentSelectItem[];
  onCardInstallmentSelect: (item: InstallmentSelectItem) => void;
  onCloseCardInstallmentBottomSheet: () => void;
}
const PaymentFormStep = ({
  cardListWrapperRef,
  isUserPaymentsInfoRefetching,
  isUserPaymentsInfoFetched,
  point,
  cardCompanyButton,
  treatmentRequest,
  userPaymentsInfo,
  requestAgreementList,
  isRequestDiagnosisLoading,
  selectCard,
  selectCardCompanyButton,
  toggleRequestUsePoint,
  openCardInstallmentListBottomSheet,
  addCreditCard,
  addKakaopay,
  toggleTreatmentRequestAgreement,
  toggleTreatmentRequestAllAgreement,
  moveToAgreementLink,
  onSubmitRequestDiagnosis,
  scrollToBottomAndCheckAllAgreement,
  isCardRegisterBottomSheetOpen,
  onCloseCardRegisterBottomSheet,
  onSuccessCardRegister,
  isCardInstallmentBottomSheetOpen,
  cardInstallments,
  onCardInstallmentSelect,
  onCloseCardInstallmentBottomSheet,
}: Props) => {
  const isRequestButtonDisabled = useMemo(() => {
    return (
      isRequestDiagnosisLoading ||
      !treatmentRequest.validatePaymentStep() ||
      !treatmentRequest.validateDiagnosisStepByWebDiagnosis() ||
      !userPaymentsInfo.getValidationInstallmentByTreatmentRequest(treatmentRequest)
    );
  }, [isRequestDiagnosisLoading, treatmentRequest, userPaymentsInfo]);

  return (
    <>
      <Wrapper>
        <RequestSection>
          <RequestSection.Header iconName={'credit_card'} title={'결제 수단'} />
          <RequestSection.ContentBody>
            <RequestSection.CardList
              ref={cardListWrapperRef}
              isPaymentsLoading={!isUserPaymentsInfoFetched && isUserPaymentsInfoRefetching}
              point={point}
              cardCompanyButton={cardCompanyButton}
              treatmentRequest={treatmentRequest}
              userPaymentsInfo={userPaymentsInfo}
              onSelectCard={selectCard}
              onSelectCardCompany={selectCardCompanyButton}
              onToggleRequestUsePoint={toggleRequestUsePoint}
              onOpenCardInstallmentBottomSheet={openCardInstallmentListBottomSheet}
              onAddCreditCard={addCreditCard}
              onAddKakaopay={addKakaopay}
              isWebTelemedicineFlow={true}
            />
          </RequestSection.ContentBody>
        </RequestSection>
        <RequestSection hiddenBorder>
          <RequestSection.Header iconName={'checklist'} title={'약관 동의'} />
          <RequestSection.ContentBody>
            <RequestSection.AgreementList
              list={requestAgreementList}
              onSelectAgreement={toggleTreatmentRequestAgreement}
              onSelectAllAgreement={toggleTreatmentRequestAllAgreement}
              onMoveOutlink={moveToAgreementLink}
              isWebTelemedicineFlow={true}
            />
          </RequestSection.ContentBody>
        </RequestSection>
      </Wrapper>
      <StickyButton>
        {!treatmentRequest.isDisabledApproveCheckButton() ? (
          <ClickLogger
            params={{
              eventName: EWebTreatmentRequestLog.ClickSubmitDiagnosis,
              payload: {
                contactType: treatmentRequest.form.contactType,
              },
            }}
          >
            <Button
              buttonColor={'primary'}
              size={'xl'}
              className={'w-full'}
              disabled={isRequestButtonDisabled}
              onClick={onSubmitRequestDiagnosis}
            >
              {isRequestDiagnosisLoading ? (
                <Loader />
              ) : !treatmentRequest.isDisabledApproveCheckButton() && !treatmentRequest.form.paymentId ? (
                '결제 수단을 등록해주세요'
              ) : (
                '진료 신청'
              )}
            </Button>
          </ClickLogger>
        ) : (
          <ClickLogger
            params={{
              eventName: TreatmentRequestLog.RequestClickAutoAgreementCheck,
            }}
          >
            <Button
              buttonColor={'gray'}
              size={'xl'}
              className={'w-full'}
              disabled={!treatmentRequest.isDisabledApproveCheckButton()}
              onClick={scrollToBottomAndCheckAllAgreement}
            >
              진료 약관 확인 및 동의
            </Button>
          </ClickLogger>
        )}
      </StickyButton>

      <CardRegisterBottomSheet
        isOpen={isCardRegisterBottomSheetOpen}
        onClose={onCloseCardRegisterBottomSheet}
        onSuccessCardRegister={onSuccessCardRegister}
      />

      <CardInstallmentBottomSheet
        open={isCardInstallmentBottomSheetOpen}
        title={'할부 기간을 선택해주세요'}
        list={cardInstallments}
        onClick={onCardInstallmentSelect}
        getItemName={(item) => item.text}
        isSelected={(item) => treatmentRequest.form.installmentMonth === item.installmentMonth}
        onClose={onCloseCardInstallmentBottomSheet}
      />
    </>
  );
};

export default PaymentFormStep;

const Wrapper = styled.div`
  min-height: calc(100dvh - 60px);
  overflow-y: scroll;
  padding-bottom: 140px;
`;
